<template>
  <div
    v-if="open"
    class="dialog-layer"
    @mousedown.self="pressedOnBackdrop = true"
    @mouseup.self="closeFromBackdrop"
  >
    <div
      ref="dialog"
      class="dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-inquiry-title"
      tabindex="-1"
    >
      <header>
        <div>
          <p>새 문의</p>
          <h2 id="trial-inquiry-title">체험 문의 등록</h2>
        </div>
        <button type="button" aria-label="문의 등록 닫기" @click="requestClose">
          ×
        </button>
      </header>

      <form @submit.prevent="submit">
        <p class="notice">
          문의 정보만 저장합니다. 수업 예약, 문자, Slack 알림은 보내지 않습니다.
        </p>
        <p v-if="error" class="error" role="alert">{{ error }}</p>

        <div class="grid">
          <label>
            <span>이름 <b>필수</b></span>
            <input
              ref="firstInput"
              v-model.trim="form.name"
              type="text"
              maxlength="80"
              autocomplete="name"
              :disabled="saving"
            />
          </label>
          <label>
            <span>전화번호 <b>필수</b></span>
            <input
              v-model.trim="form.phone_number"
              type="tel"
              maxlength="20"
              autocomplete="tel"
              placeholder="010-0000-0000"
              :disabled="saving"
            />
          </label>
          <label>
            <span>처음 알게 된 경로</span>
            <select v-model="form.inquiry_source" :disabled="saving">
              <option
                v-for="source in inquirySources"
                :key="source.value"
                :value="source.value"
              >
                {{ source.label }}
              </option>
            </select>
          </label>
          <label>
            <span>문의 경로</span>
            <select v-model="form.booking_source" :disabled="saving">
              <option
                v-for="source in bookingSources"
                :key="source.value"
                :value="source.value"
              >
                {{ source.label }}
              </option>
            </select>
          </label>
          <label class="wide">
            <span>담당 코치</span>
            <select v-model="form.assigned_coach_id" :disabled="saving">
              <option value="">미지정</option>
              <option
                v-for="coach in coaches"
                :key="coach.id"
                :value="coach.id"
              >
                {{ coach.name }}
              </option>
            </select>
          </label>
        </div>

        <fieldset>
          <legend>운동 목적 <small>복수 선택</small></legend>
          <div class="chips">
            <label
              v-for="goal in goals"
              :key="goal.value"
              class="chip"
              :class="{ checked: form.goals.includes(goal.value) }"
            >
              <input
                v-model="form.goals"
                type="checkbox"
                :value="goal.value"
                :disabled="saving"
              />
              {{ goal.label }}
            </label>
          </div>
        </fieldset>

        <p v-if="validationError" class="error" role="alert">
          {{ validationError }}
        </p>

        <footer>
          <button
            type="button"
            class="ghost"
            :disabled="saving"
            @click="requestClose"
          >
            취소
          </button>
          <button type="submit" class="primary" :disabled="saving">
            {{ saving ? "등록 중…" : "문의 등록" }}
          </button>
        </footer>
      </form>
    </div>
  </div>
</template>

<script>
import { BOOKING_SOURCES, GOALS, INQUIRY_SOURCES } from "@/lib/trialCRM";

const emptyForm = () => ({
  name: "",
  phone_number: "",
  inquiry_source: "unknown",
  booking_source: "phone",
  goals: [],
  assigned_coach_id: "",
});

export default {
  name: "TrialInquiryDialog",
  props: {
    open: { type: Boolean, default: false },
    coaches: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  emits: ["close", "create", "dirty-change"],
  data() {
    return {
      inquirySources: INQUIRY_SOURCES,
      bookingSources: BOOKING_SOURCES,
      goals: GOALS,
      form: emptyForm(),
      validationError: "",
      pressedOnBackdrop: false,
      returnFocusTo: null,
    };
  },
  computed: {
    dirty() {
      return JSON.stringify(this.form) !== JSON.stringify(emptyForm());
    },
  },
  watch: {
    open(open) {
      if (!open) return;
      this.returnFocusTo = document.activeElement;
      this.form = emptyForm();
      this.validationError = "";
      this.$nextTick(() => this.$refs.firstInput?.focus());
    },
    dirty(value) {
      this.$emit("dirty-change", value);
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  methods: {
    requestClose() {
      if (this.saving) return;
      if (this.dirty && !window.confirm("작성 중인 문의를 버릴까요?")) return;
      this.$emit("close");
      this.$nextTick(() => this.returnFocusTo?.focus?.());
    },
    closeFromBackdrop() {
      if (this.pressedOnBackdrop) this.requestClose();
      this.pressedOnBackdrop = false;
    },
    onBeforeUnload(event) {
      if (!this.open || !this.dirty) return;
      event.preventDefault();
      event.returnValue = "";
    },
    onKeydown(event) {
      if (!this.open) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.requestClose();
        return;
      }
      if (event.key !== "Tab") return;
      const items = [
        ...(this.$refs.dialog?.querySelectorAll(
          "button:not([disabled]), input:not([disabled]), select:not([disabled])"
        ) || []),
      ];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    submit() {
      if (this.saving) return;
      const digits = this.form.phone_number.replace(/\D/g, "");
      if (!this.form.name) {
        this.validationError = "이름을 입력해 주세요.";
        return;
      }
      if (digits.length < 9 || digits.length > 11) {
        this.validationError = "전화번호를 확인해 주세요.";
        return;
      }
      this.validationError = "";
      this.$emit("create", {
        ...this.form,
        phone_number: this.form.phone_number.trim(),
        assigned_coach_id: this.form.assigned_coach_id || null,
        goals: [...this.form.goals],
      });
    },
  },
};
</script>

<style scoped>
.dialog-layer {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(2px);
}
.dialog {
  width: min(610px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  border: 1px solid #343839;
  border-radius: 16px;
  outline: none;
  background: #17191a;
  color: #fff;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
}
header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 22px 16px;
  border-bottom: 1px solid #2c3031;
}
header p {
  margin: 0 0 3px;
  color: #78ff47;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
}
h2 {
  margin: 0;
  font-size: 21px;
}
header button {
  width: 38px;
  height: 38px;
  border: 1px solid #383c3d;
  border-radius: 9px;
  background: #1e2122;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
}
form {
  padding: 20px 22px 22px;
}
.notice,
.error {
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}
.notice {
  border: 1px solid #37463a;
  background: #182119;
  color: #a8c4ae;
}
.error {
  border: 1px solid #744045;
  background: #321e20;
  color: #ffadb2;
}
.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
}
label {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  color: #bec3c0;
  font-size: 12px;
}
label span {
  display: flex;
  justify-content: space-between;
}
label b {
  color: #78ff47;
  font-size: 10px;
}
.wide {
  grid-column: 1 / -1;
}
input,
select {
  width: 100%;
  height: 43px;
  padding: 0 11px;
  border: 1px solid #353a3b;
  border-radius: 8px;
  outline: none;
  background: #111314;
  color: #fff;
  font: inherit;
}
input:focus,
select:focus {
  border-color: #78ff47;
  box-shadow: 0 0 0 2px rgba(120, 255, 71, 0.1);
}
fieldset {
  margin: 17px 0 0;
  padding: 0;
  border: 0;
}
legend {
  margin-bottom: 8px;
  color: #bec3c0;
  font-size: 12px;
}
legend small {
  color: #747b77;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.chip {
  position: relative;
  padding: 7px 10px;
  border: 1px solid #383d3e;
  border-radius: 999px;
  color: #a9afab;
  cursor: pointer;
}
.chip input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.chip.checked {
  border-color: #78ff47;
  background: rgba(120, 255, 71, 0.1);
  color: #b9ff9f;
}
footer {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  margin-top: 22px;
}
footer button {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
}
.ghost {
  border: 1px solid #3b4041;
  background: #1d2021;
  color: #fff;
}
.primary {
  border: 1px solid #78ff47;
  background: #78ff47;
  color: #0b1308;
}
button:disabled {
  opacity: 0.48;
  cursor: not-allowed;
}
@media (max-width: 560px) {
  .dialog-layer {
    align-items: end;
    padding: 0;
  }
  .dialog {
    width: 100%;
    max-height: 100vh;
    border-width: 1px 0 0;
    border-radius: 16px 16px 0 0;
  }
  .grid {
    grid-template-columns: 1fr;
  }
  .wide {
    grid-column: auto;
  }
}
</style>
